/**
 * WHAT IS IN THE LEFT RAIL, AND WHY IT IS NOT A LIST OF SCREENS.
 *
 * The founder, 13 September 2026, with a mock-up:
 *
 *   "New interface design for the Portal. And let's make sure that it can
 *    collapse the 'card' menu items on the far left such that expanded the full
 *    module names show, and collapsed only the icons show."
 *
 * THE RAIL IS BUILT FROM WHAT THE PERSON HOLDS, NEVER FROM A LIST WRITTEN DOWN
 * HERE. Canon 1.3: the card is a door and the permission is what determines
 * what is behind it. A hardcoded rail would be a second answer to the question
 * `fn_my_admin_cards()` already answers, and the two would disagree the first
 * time a grant changed — the person would see a destination that opens to
 * nothing, or miss one they were given this morning.
 *
 * So this takes the cards the database returned and turns them into
 * destinations. It invents nothing. A Portal drawn for somebody holding no
 * permissions has an empty rail, which is correct and not a fault.
 *
 * PROHIBITION 5. Nothing here compares a role name. `is_master` is a property
 * of the CARD the database handed back, not a role this code recognised — the
 * grant decided it before this file saw anything.
 *
 * THE ONE ENTRY THAT IS NOT A CARD. Field onboarding is a standing panel rather
 * than a card of its own, and it is the thing Orchid is working on right now,
 * so it earns a rail entry — but only for somebody who holds a permission that
 * opens it. That list is passed in rather than restated here, so it cannot
 * drift from the panel's own gate.
 *
 * OVERVIEW WENT AND CAME BACK, IN ONE DAY, AND BOTH WERE RULINGS.
 *
 *   "The overview screen seems unnecessary. just start the list from the
 *    onboarding part and work your way down on the left menu panel."
 *
 *   "you can put it back, but just remove the Field Onboarding part from it,
 *    because that has its own screen now."
 *
 * The first was right about the CARDS: the grid and this rail were one answer
 * drawn twice. It was never right about the field onboarding panel, which was
 * the thing the grid carried that the rail could not — and that is why removing
 * Overview left the panel sitting at `/` on its own. Giving the panel its own
 * address resolves both: the grid comes back to `/`, the panel moves to
 * `/field-onboarding`, and neither is a copy of anything.
 *
 * WHICH OF THE TWO COMES FIRST IS A REAL CHOICE AND THIS IS THE CONSERVATIVE
 * ONE. Overview leads, because it is the way back and "put it back" reads as
 * restoring what was there. The earlier "start the list from the onboarding
 * part" was said when there was no Overview to start from. If field onboarding
 * should still lead, it is the order of two pushes below and nothing else.
 */

import {
  LayoutGrid, Users, ShieldCheck, FileSearch, Scale, Landmark, BarChart3,
  UserCog, Bot, Headphones, Megaphone, Settings, Terminal, Lightbulb,
  Building2, Diamond, Crown, BookOpen,
} from 'lucide-react';
import { SURFACES } from './sections';

/**
 * A card's icon.
 *
 * These are the same meanings the dashboard's text marks already carry — this
 * is not a second vocabulary, it is the same one drawn at a size that survives
 * being the only thing on screen. A slug with no entry gets `Diamond`, which is
 * what the dashboard's `MARKS` already falls back to, so a card added to the
 * database tomorrow appears in the rail immediately rather than being invisible
 * until somebody edits this file.
 */
export const CARD_ICONS = {
  compliance:          ShieldCheck,
  internal_audit:      FileSearch,
  legal_disputes:      Scale,
  finance:             Landmark,
  business_intel:      BarChart3,
  human_capital:       UserCog,
  agentic_capital:     Bot,
  support_centre:      Headphones,
  marketing:           Megaphone,
  system_operations:   Settings,
  developer_console:   Terminal,
  orchid_insights:     Lightbulb,
  orchid_developments: Building2,
};

export const iconForCard = (slug) => CARD_ICONS[slug] || Diamond;

/**
 * The permissions that open the field onboarding panel.
 *
 * DERIVED FROM `SURFACES`, NOT RESTATED. `TheStand` already computes its gate
 * this way — `standsAlone` is the property that says a capability lives above
 * the grid rather than inside a card — and a second list written out here would
 * put a rail entry in front of somebody the panel refuses, which is a door onto
 * nothing. A fourth permission added to SURFACES tomorrow lights up both.
 */
export const permissionsThatStandAlone = () =>
  Object.entries(SURFACES).filter(([, s]) => s.standsAlone).map(([key]) => key);

/**
 * THE RAIL, IN THE ORDER IT IS DRAWN.
 *
 * Overview first and always: it is the way back, and a rail whose first entry
 * can be missing is one whose shape changes under somebody between two visits.
 *
 * Then Resources, also always — the second of the two entries with no gate on
 * them. The shelf is readable by every active member of staff and by no
 * permission at all, so it is true for whoever is looking at it.
 *
 * Then field onboarding, where it is held, at an address of its own.
 *
 * Then the cards, IN THE ORDER THE DATABASE GAVE THEM. `fn_my_admin_cards()`
 * carries sort_order and has already applied it; re-sorting here by title would
 * throw away a decision somebody made in the catalogue and replace it with the
 * alphabet.
 *
 * The master card last, because it is a different kind of thing — everything
 * above it is a room, and it is the view over all of them.
 *
 * AND THE FIRST ENTRY IS ALWAYS THERE AGAIN. While Overview was gone, somebody
 * who did not hold field onboarding had no entry pointing at `/` at all — a
 * front door their own menu did not name. Overview returning closes that.
 */
export function railDestinations({ cards, permissions } = {}) {
  const all = cards || [];
  const held = (permissions || []).map((p) => p.permission_key);

  const out = [{
    key: 'overview',
    label: 'Overview',
    to: '/',
    Icon: LayoutGrid,
    // The only entry that must match the path exactly. Every other destination
    // owns a subtree; '/' is a prefix of every path there is.
    exact: true,
  }, {
    // RESOURCES, SECOND, AND THE SECOND ENTRY WITH NO GATE ON IT.
    //
    //   "we should probably add one last card to the Overview screen of the
    //    Portal: Resources. That's where every single company document can be
    //    found - SOPs, departmental Welcome Packs, HR resources, processes and
    //    procedures, etc."
    //
    // He called it the last CARD and on the Overview grid it is exactly that.
    // Here it is second, because this rail already has a rule about its end:
    // the master card is last, "because it is the view over all of them", and
    // pushing a shelf past it would quietly overturn that.
    //
    // Second is where it belongs on its own merits. The rail now reads
    // ungated, ungated, then the rooms somebody was granted, then the view
    // over the rooms — and the two at the top are the two that are true for
    // everybody. `portal_documents` is readable by every ACTIVE member of
    // staff and by no permission at all (script 59's ruling, kept by script
    // 330), so a new joiner holding nothing sees Overview and Resources and
    // that is the correct first morning: the welcome pack that explains what
    // Orchid is was written for precisely them, and a gate here would hide it
    // from the only person who needs it most.
    //
    // THE DATABASE STILL DECIDES WHAT COMES BACK. fn_portal_resources checks
    // admin_users.status = 'active' on every read and returns nothing at all
    // to anybody else. This entry is a door; it is not the lock.
    key: 'resources',
    label: 'Resources',
    to: '/resources',
    Icon: BookOpen,
    exact: false,
  }];

  const standsAlone = permissionsThatStandAlone();
  if (standsAlone.some((p) => held.includes(p))) {
    out.push({
      key: 'field_onboarding',
      label: 'Field Onboarding',
      // AN ADDRESS OF ITS OWN, which is what changed today. This pointed at
      // Overview and scrolled to an anchor, which is why an entry carrying an
      // `anchor` had to be skipped when working out where somebody was. It is a
      // plain destination now and marks itself like any other.
      to: '/field-onboarding',
      Icon: Users,
      exact: false,
    });
  }

  all.filter((c) => !c.is_master).forEach((c) => {
    out.push({
      key: c.slug,
      label: c.title,
      to: `/card/${c.slug}`,
      Icon: iconForCard(c.slug),
      exact: false,
    });
  });

  const master = all.find((c) => c.is_master);
  if (master) {
    out.push({
      key: 'master',
      label: master.title,
      to: '/master',
      Icon: Crown,
      exact: false,
    });
  }

  return out;
}

/**
 * Which entry the current address is inside.
 *
 * Returns a key rather than a boolean per entry, so exactly one entry can be
 * current: two highlighted rows is a rail that has stopped telling somebody
 * where they are.
 *
 * The longest matching `to` wins, so that a future '/card/finance/payouts'
 * marks Finance rather than the card itself.
 *
 * THE SHORTCUT RULE THAT USED TO LIVE HERE IS GONE WITH THE THING IT GUARDED.
 * While Overview existed, field onboarding pointed at it and both matched '/',
 * so an entry carrying an `anchor` was skipped — otherwise which of the two
 * highlighted came down to the order of a loop. No destination carries an
 * anchor now, so that rule could no longer fire, and a rule that cannot fire is
 * one nobody can tell is broken. Removed rather than left standing.
 */
export function currentRailKey(destinations, pathname) {
  const path = pathname || '/';
  let best = null;
  (destinations || []).forEach((d) => {
    const route = d.to;
    const hit = d.exact ? path === route : (path === route || path.startsWith(`${route}/`));
    if (!hit) return;
    if (!best || route.length > best.route.length) best = { key: d.key, route };
  });
  return best ? best.key : null;
}
